import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/common/refactoredComponents/button/Button';
import Datatable from 'src/common/refactoredComponents/dataTable/DataTable';
import { ButtonStyles } from 'src/constants/buttonStyles';
import { accountNumberZeroTrimmer } from 'src/utils/util';
import {
    SubContent,
    Description,
    SetNumber,
    StrongLabel,
    TableCard,
    TableCellContent,
    ViewSetAction,
    SetBatchLink,
} from './index.styled';
import {
    SETS_DEFAULT_TABLE_SORT_FIELD,
    SETS_PAGE_LIMIT,
    SETS_TABLE_COLUMNS,
} from './setsInventoryConstants';
import {
    GREY_PRODUCT_LIST_COLOR,
    SMALL_TEXT_PX,
    WHITE_FONT_COLOR,
} from 'src/styles/fonts/MCFontConstants';

const getTextValue = (value) =>
    value === undefined || value === null ? '' : String(value).trim();

const getTrimmedAccountNumber = (value) => {
    const accountNumber = getTextValue(value);
    return accountNumber
        ? accountNumberZeroTrimmer(accountNumber.replace(/-/g, ''))
        : '';
};

const splitAccountValue = (value) => {
    const accountValue = getTextValue(value);
    const accountParts = accountValue.match(/^([0-9-]+)\s*-?\s*(.*)$/);

    if (accountParts) {
        return {
            accountName: getTextValue(accountParts[2]),
            accountNumber: getTrimmedAccountNumber(accountParts[1]),
        };
    }

    const trailingAccountParts = accountValue.match(/^(.*?)\s+([0-9-]+)$/);

    if (!trailingAccountParts) {
        return { accountName: accountValue, accountNumber: '' };
    }

    return {
        accountName: getTextValue(trailingAccountParts[1]),
        accountNumber: getTrimmedAccountNumber(trailingAccountParts[2]),
    };
};

const formatLocationAccount = (
    accountName,
    accountNumber,
    fallbackLocation
) => {
    const trimmedName = getTextValue(accountName);
    const trimmedNumber = getTrimmedAccountNumber(accountNumber);

    if (trimmedName || trimmedNumber) {
        return [trimmedName, trimmedNumber].filter(Boolean).join(' ');
    }

    const parsedLocation = splitAccountValue(fallbackLocation);
    return [parsedLocation.accountName, parsedLocation.accountNumber]
        .filter(Boolean)
        .join(' ');
};

const SetsTable = ({
    rows,
    totalRecords,
    recordsPerPage = SETS_PAGE_LIMIT,
    first = 0,
    onPageChange = () => {},
    onViewSet = () => {},
    onSortChange = () => {},
    sortField = SETS_DEFAULT_TABLE_SORT_FIELD,
    sortOrder = 1,
    handleBatchClick = () => {},
}) => {
    const { t } = useTranslation();
    const tableContainerRef = useRef(null);
    const isFullPage = (rows?.length || 0) >= recordsPerPage;

    const scrollTableToTop = () => {
        const container = tableContainerRef.current;

        if (!container) return;

        [
            container,
            container.querySelector('.p-datatable-wrapper'),
            container.querySelector('.p-datatable-scrollable-body'),
        ].forEach((scrollElement) => {
            if (scrollElement) {
                scrollElement.scrollTop = 0;
            }
        });
        container.scrollIntoView({ block: 'start', behavior: 'auto' });
    };

    const handlePageChange = (event) => {
        scrollTableToTop();
        onPageChange(event);
    };

    const columns = SETS_TABLE_COLUMNS.map((column) => {
        const translatedColumn = {
            ...column,
            header: column.headerKey ? t(column.headerKey) : column.header,
        };

        if (column.field === 'setNumber') {
            return {
                ...translatedColumn,
                body: (rowData) => (
                    <TableCellContent>
                        {rowData.setBatch ? (
                                    <SetBatchLink
                                        href="#"
                                        onClick={(event) => handleBatchClick(event, rowData)}
                                    >
                                        {rowData.setNumber + "." + rowData?.setBatch}
                                    </SetBatchLink>) : (
                                    <SetNumber>{rowData.setNumber}</SetNumber>
                                )}
                        <Description $isTableDescription>
                            {rowData.description}
                        </Description>
                        {getTextValue(rowData.brand) && (
                            <SubContent>
                                <StrongLabel>
                                    {t(
                                        'receiveSets.setsInventory.fieldLabels.brand'
                                    )}
                                </StrongLabel>{' '}
                                {rowData.brand}
                            </SubContent>
                        )}
                    </TableCellContent>
                ),
            };
        }

        if (column.field === 'referenceNumbers') {
            return {
                ...translatedColumn,
                body: (rowData) => (
                    <TableCellContent>
                        {rowData?.nextSurgerysstRefNo && (
                        <SubContent>
                            <StrongLabel>
                                {t(
                                    'receiveSets.setsInventory.fieldLabels.refNumber'
                                )}
                            </StrongLabel>{' '}
                            {getTextValue(rowData.nextSurgerysstRefNo) || ''}
                        </SubContent>)}
                        {rowData?.nextSurgerySapOrder && (
                        <SubContent>
                            <StrongLabel>
                                {t(
                                    'receiveSets.setsInventory.fieldLabels.orderNumber'
                                )}
                            </StrongLabel>{' '}
                            {getTextValue(rowData.nextSurgerySapOrder) || 'NA'}
                        </SubContent>)}
                    </TableCellContent>
                ),
            };
        }

        if (column.field === 'status') {
            return {
                ...translatedColumn,
                body: (rowData) => (
                    <TableCellContent>
                        <SubContent>{rowData.status}</SubContent>
                    </TableCellContent>
                ),
            };
        }

        if (column.field === 'location') {
            return {
                ...translatedColumn,
                body: (rowData) => (
                    <TableCellContent>
                        <SubContent>
                            <StrongLabel>
                                {t(
                                    'receiveSets.setsInventory.fieldLabels.home'
                                )}
                            </StrongLabel>{' '}
                            {formatLocationAccount(
                                rowData.homeAccName,
                                rowData.homeAccNum,
                                rowData.homeLocation
                            )}
                        </SubContent>
                        <SubContent>
                            <StrongLabel>
                                {t(
                                    'receiveSets.setsInventory.fieldLabels.current'
                                )}
                            </StrongLabel>{' '}
                            {formatLocationAccount(
                                rowData.currentAccName,
                                rowData.currentAccNum,
                                rowData.currentLocation
                            )}
                        </SubContent>
                    </TableCellContent>
                ),
            };
        }

        if (column.field === 'action') {
            return {
                ...translatedColumn,
                body: (rowData) => (
                    <ViewSetAction>
                        <Button
                            id={`receiveSets-setsInventory-viewSet-${rowData.id}`}
                            variant={ButtonStyles.secondary}
                            borderRadius="20px"
                            height="32px"
                            fontSize={SMALL_TEXT_PX}
                            fontFamily="AvenirNextWorld Demi"
                            padding="0 12px"
                            WhiteSpace="nowrap"
                            clicked={() => onViewSet(rowData)}
                        >
                            {t('receiveSets.setsInventory.viewSet')}
                        </Button>
                    </ViewSetAction>
                ),
            };
        }

        return translatedColumn;
    });

    return (
        <TableCard $isFullPage={isFullPage} ref={tableContainerRef}>
            <Datatable
                id="receiveSets-setsInventory-datatable"
                columns={columns}
                data={rows}
                totalRecords={totalRecords}
                rows={recordsPerPage}
                first={first}
                onPageChange={handlePageChange}
                onSort={onSortChange}
                paginator={totalRecords > 0}
                keyValue="id"
                sortField={sortField}
                sortOrder={sortOrder}
                isListView
                sortMargin="2px"
                displayType="inline-table"
                columnHeight="87px"
                padding="8px 14px"
                headerPadding="0 14px"
                defaultScrollHeight="calc(100vh - 22rem)"
                backgroundColorOdd={WHITE_FONT_COLOR}
                backgroundColorEven={GREY_PRODUCT_LIST_COLOR}
                backgroundColorPagination={GREY_PRODUCT_LIST_COLOR}
                paginationInfoText={t('receiveSets.setsInventory.showingSets')}
                rightCOntentMarginTop={5}
            />
        </TableCard>
    );
};

export default SetsTable;
